import React, { useState, useContext } from 'react';
import GoogleMapReact from 'google-map-react';
import Wayfinding from './Wayfinding';
import Marker from './Marker';
import areaLabels from '../constants/areaLabels';
import campusAreas from '../constants/GeoJSON/campusAreas';
import parkingLots from '../constants/GeoJSON/parkingLots';
import mallLevel from '../constants/GeoJSON/mallLevel';
import { markers, tourMarkerKeys } from '../constants/markers';
import { campusMarkers } from '../constants/campuses';
import { GlobalContext } from '../context/GlobalState';

const DEFAULT_ZOOM = 17;

export default function Maps() {
  const defaultProps = {
    // ndc
    center: {
      lat: 49.92,
      lng: -97.21122,
    },
    zoom: 17,
  };

  const {
    store: { center, zoom, selectedMarkers, wayfinding },
    setMapCenter,
    updateMap,
  } = useContext(GlobalContext);

  const [map, setMap] = useState({}); // Google Map Object
  const [parkingLotFeatures, setParkingLotFeatures] = useState([]); // Parking Lots
  const [mallLevelFeatures, setMallLevelFeatures] = useState([]); // Mall Level
  const [popper, setPopper] = useState(''); // Marker Popper
  const closePopper = () => {
    setPopper('');
  };

  const setDataLayerStyle = (map) => {
    map.data.setStyle(function (feature) {
      const fillColor = feature.getProperty('fill');
      const fillOpacity = feature.getProperty('fill-opacity');
      const strokeColor = feature.getProperty('stroke');
      const strokeWeight = feature.getProperty('stroke-width');
      const strokeOpacity = feature.getProperty('stroke-opacity');
      return /** @type {!google.maps.Data.StyleOptions} */ {
        fillColor,
        fillOpacity,
        strokeColor,
        strokeWeight,
        strokeOpacity,
      };
    });
  };

  const setLayerVisibility = (features, visible) => {
    if (map && map.data) {
      features.forEach((feature) => {
        map.data.overrideStyle(feature, {
          visible,
          zIndex: 1,
        });
      });
    }
  };

  const loadAreaLabels = (minZoom) => {
    if (zoom < minZoom) return null;

    return areaLabels.map((label) => {
      const key = `${label.lat}${label.lng}`;
      return (
        <Marker
          lat={label.lat}
          lng={label.lng}
          marker={label}
          zoom={zoom}
          popperOpen={label.info && popper === key}
          closePopper={closePopper}
          key={key}
        />
      );
    });
  };

  const loadDataLayer = ({ map }) => {
    setMap(map);
    map.data.addGeoJson(campusAreas);
    setDataLayerStyle(map);

    // Load Parking Lots and set it invisible
    const parkingLotsLayer = map.data.addGeoJson(parkingLots);
    setParkingLotFeatures(parkingLotsLayer);
    setLayerVisibility(parkingLotsLayer, false, map);

    // Load Mall Level and set it invisible
    const mallLevelLayer = map.data.addGeoJson(mallLevel);
    setMallLevelFeatures(mallLevelLayer);
    setLayerVisibility(mallLevelLayer, false, map);
  };

  const loadCampusMarkers = () => {
    return Object.keys(campusMarkers).map((key) => {
      const marker = campusMarkers[key];
      return (
        <Marker lat={marker.lat} lng={marker.lng} marker={marker} key={key} />
      );
    });
  };

  const handleMarkerClick = (key, center) => {
    setMapCenter(center);
    setPopper(key);
  };

  const loadMarkers = (minZoom) => {
    if (zoom < minZoom) {
      setLayerVisibility(mallLevelFeatures, false, map);
      return null;
    }

    const markersToRender = selectedMarkers.slice();
    if (tourMarkerKeys.some((key) => selectedMarkers.includes(key))) {
      markersToRender.push('commonArea');
    }

    // hide parking lots area
    let isParkingLotsVisible = false,
      isMallLevelVisible = false;
    if (selectedMarkers.includes('parking')) {
      isParkingLotsVisible = true;
    }
    // hide mall level area
    if (selectedMarkers.includes('directions')) {
      isMallLevelVisible = true;
    }

    setLayerVisibility(parkingLotFeatures, isParkingLotsVisible, map);
    setLayerVisibility(mallLevelFeatures, isMallLevelVisible, map);

    return markersToRender.map((name) => {
      return markers[name].items.map((marker) => {
        const key = `${marker.lat}${marker.lng}`;
        return (
          <Marker
            name={name}
            lat={marker.lat}
            lng={marker.lng}
            marker={marker}
            key={key}
            popperOpen={popper === key}
            closePopper={closePopper}
          />
        );
      });
    });
  };

  return (
    // Important! Always set the container height explicitly
    <div style={{ height: '100vh', width: '100vw' }}>
      <GoogleMapReact
        bootstrapURLKeys={{ key: 'AIzaSyCbS73m2vp8bKIAet7gU9HJvTfR4tzLYc0' }}
        defaultCenter={defaultProps.center}
        center={center}
        defaultZoom={defaultProps.zoom}
        zoom={zoom}
        onChange={updateMap}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={loadDataLayer}
        onChildClick={handleMarkerClick}
        options={() => ({
          fullscreenControl: false,
          zoomControl: false,
        })}
      >
        {loadCampusMarkers()}
        {loadAreaLabels(DEFAULT_ZOOM)}
        {loadMarkers(DEFAULT_ZOOM)}
        {wayfinding && <Wayfinding lat={wayfinding.lat} lng={wayfinding.lng} />}
      </GoogleMapReact>
    </div>
  );
}
